import React, { useMemo, useState } from 'react';

import { SearchIcon, PlusIcon } from '@vlabs/icons';
import { Control } from '@vlabs/uikit';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { DEFAULT_GROUP_LABEL } from './constants';
import {
  useFilterByNameGroupOptions,
  useVisibleGroupOptions,
} from './hooks';
import { useOptionsFilterController } from './hooks/useOptionsFilterController';
import { DynamicOptionInput } from './options/DynamicOptionInput';
import { OptionItem } from './options/OptionItem';
import st from './OptionsFilter.module.sass';
import type {
  OptionGroup,
  Option,
} from './types';
import { convertToOptionGroups } from './utils/convertToOptionGroups';

interface OptionsFilterProps<O extends Option = Option> {
  value?: O['value'][];
  defaultValue?: O['value'][];
  onChange?: (value: O['value'][]) => void;

  name?: string | undefined;
  label?: string | undefined;
  options: OptionGroup<O> | OptionGroup<O>[] | O[];
  isCreatable?: boolean | undefined;
  isSearchable?: boolean | undefined;
  visibleCount?: number | undefined;
  validateProp?: ((_: string) => string | boolean) | undefined;
}

export function OptionsFilter<O extends Option = Option>({
  value = undefined,
  defaultValue = [],
  onChange = undefined,
  name = 'options_filter',
  options,
  label = undefined,
  isCreatable = false,
  isSearchable = true,
  visibleCount = undefined,
  validateProp = undefined,
}: OptionsFilterProps<O>): React.JSX.Element {
  const { t } = useTranslation();
  const optionGroups = useMemo(() => convertToOptionGroups(options), [options]);
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [dynamicOptionError, setDynamicOptionError] = useState<string | undefined>(undefined);

  const { addDynamicOption, controllableOptionGroups } = useOptionsFilterController({
    value,
    defaultValue,
    onChange,
    isCreatable,
    optionGroups,
  });

  const { filteredOptions, searchValue, onChangeSearchValue } = useFilterByNameGroupOptions({
    optionGroups: controllableOptionGroups,
  });

  const { visibleOptionGroups, showMore } = useVisibleGroupOptions({
    optionGroups: filteredOptions,
    step: visibleCount,
  });

  const shouldShowGroupLabels = filteredOptions.some(
    (group) => group.label !== DEFAULT_GROUP_LABEL,
  );

  const handleCancelAdding = () => {
    setDynamicOptionError(undefined);
    setIsAdding(false);
  };

  const handleStartAdding = () => {
    setDynamicOptionError(undefined);
    setIsAdding(true);
  };

  const handleSubmitDynamicOption = (nextValue: string) => {
    const result = addDynamicOption(nextValue);

    if (typeof result === 'string') {
      setDynamicOptionError(result);
      return;
    }

    setDynamicOptionError(undefined);
    setIsAdding(false);
  };
  return (
    <React.Fragment>
      {!!label && <label className="Subtitle-2">{label}</label>}

      {!!isSearchable && (
        <Control.Input
          className={st.SearchInput}
          icon={<SearchIcon />}
          isClearable
          name={`${name}_search`}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChangeSearchValue(e.target.value)}
          onClear={() => onChangeSearchValue('')}
          value={searchValue}
        />
      )}

      {visibleOptionGroups.map((group) => (
        <div
          className={cn(st.wrapper_scrollable)}
          key={group.label}
        >
          {!!shouldShowGroupLabels && (
            <div
              className={st.GroupLabel}
              data-testid="OptionsGroupLabel"
            >
              {t(group.label)?.toUpperCase()}
            </div>
          )}

          <div className={st.OptionsContainer}>
            {group.options.map((option) => (
              <OptionItem
                checked={option.isSelected}
                key={`${group.label}-${String(option.value)}`}
                name={name}
                onRemove={option.remove}
                onToggle={option.toggle}
                option={option}
              />
            ))}
          </div>
        </div>
      ))}

      <div className={st.PanelInLine}>
        {!!showMore && (
          <Control.Button
            data-testid="ShowMore"
            kind="primary"
            onClick={showMore}
            size="small"
            variant="flat"
          >
            {t('кнопка.показать ещё')}
          </Control.Button>
        )}

        {!!isCreatable && !isAdding && (
          <Control.Button
            data-testid="AddNewOption"
            icon={<PlusIcon />}
            kind="primary"
            onClick={handleStartAdding}
            size="small"
            variant="flat"
          >
            {t('кнопка.добавить')}
          </Control.Button>
        )}
      </div>

      {!!isAdding && (
        <DynamicOptionInput
          error={dynamicOptionError}
          onCancel={handleCancelAdding}
          onSubmit={handleSubmitDynamicOption}
          validate={validateProp}
        />
      )}
    </React.Fragment>
  );
}
